import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import {
  BRAND, Icon, Button, Field, Input, Card, Stepper, Frame,
} from '../../../components/onboarding/primitives';
import { onboardingAPI } from '../../../services/api';

const STEP_LABELS = ['الحساب', 'المختبر', 'الفروع', 'الباقة', 'الأجهزة', 'الدفع'];

const PHONE_RE = /^05\d{8}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Props contract:
 *   state      — full onboarding state object ({ s1, s2, s3, s4, ... }).
 *   setState   — setter; accepts either a new object or an updater fn.
 *   goToStep   — (n: number | 'dashboard') => void.
 *   goBack     — () => void. (Unused on step 1.)
 */
export default function Step1Account({ state, setState, goToStep }) {
  const d = state.s1 || {};
  const set = (k, v) => setState({ ...state, s1: { ...d, [k]: v } });

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const pwStrength = useMemo(() => {
    const p = d.password || '';
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[a-z]/.test(p)) s++;
    if (/\d/.test(p)) s++;
    return s;
  }, [d.password]);

  const strengthLabel = ['', 'ضعيفة', 'متوسطة', 'جيدة', 'قوية'][pwStrength];
  const strengthColor = ['', '#E24B4A', '#E8A13C', '#1D9E75', '#0F6E56'][pwStrength];

  const phoneOk = PHONE_RE.test(d.phone || '');
  const emailOk = EMAIL_RE.test(d.email || '');
  const matchOk = d.password && d.password === d.confirmPassword;

  const canNext = !!(
    d.nameAr && d.name && d.ownerName
    && phoneOk && emailOk
    && pwStrength >= 3
    && matchOk
    && d.agree
  );

  const handleSubmit = async () => {
    if (!canNext || submitting) return;
    setSubmitting(true);
    try {
      const res = await onboardingAPI.register({
        nameAr: d.nameAr,
        name: d.name,
        ownerName: d.ownerName,
        email: d.email,
        password: d.password,
        phone: d.phone,
      });

      // Backend wraps the payload in `{ success, message, data: {...} }`
      // via successResponse, so the real token lives at res.data.data.token.
      const payload = res?.data?.data || res?.data || {};
      const token = payload.token;
      const user = payload.user;
      const tenant = payload.tenant;
      const resumed = payload.resumed === true;

      if (token) localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', JSON.stringify(user));
      if (tenant) localStorage.setItem('tenant', JSON.stringify(tenant));

      toast.success(resumed ? 'تم استئناف التسجيل' : 'تم إنشاء حسابك بنجاح');
      goToStep(2);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message
        || err?.response?.data?.error
        || 'تعذّر إنشاء الحساب. حاول مرة أخرى.';
      if (status === 409) {
        // Email exists with a different password — push the user to login
        // instead of letting them keep hammering register.
        toast.error(msg, { duration: 5000 });
        setTimeout(() => { window.location.href = '/login'; }, 1600);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Frame
      stepper={<Stepper steps={STEP_LABELS} current={0} />}
      footer={
        <>
          <a style={{ color: BRAND.muted, fontSize: 13, textDecoration: 'none', cursor: 'pointer' }}>
            لديك حساب؟ <span style={{ color: BRAND.greenDark, fontWeight: 600 }}>سجّل الدخول</span>
          </a>
          <Button
            onClick={handleSubmit}
            disabled={!canNext || submitting}
            icon={submitting ? <Icon.Clock size={16} /> : <Icon.Arrow />}
          >
            {submitting ? 'جارٍ الإنشاء...' : 'التالي'}
          </Button>
        </>
      }
    >
      <Card elevated padding={40}>
        <h1 style={{ fontSize: 30, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.6 }}>
          ابدأ رحلتك مع LIS Techs
        </h1>
        <p style={{ fontSize: 15, color: BRAND.muted, margin: '0 0 32px' }}>
          خطوة واحدة تفصلك عن نظام احترافي لمختبرك.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Field label="اسم المختبر (عربي)" required>
            <Input value={d.nameAr} onChange={(v) => set('nameAr', v)} placeholder="مثلاً: مختبرات الصحة" />
          </Field>
          <Field label="Lab name (English)" required>
            <Input value={d.name} onChange={(v) => set('name', v)} placeholder="Al-Sehha Labs" ltr />
          </Field>
        </div>

        <Field label="الاسم الكامل لصاحب الحساب" required>
          <Input value={d.ownerName} onChange={(v) => set('ownerName', v)} placeholder="الاسم الأول والعائلة" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Field
            label="رقم الجوال"
            required
            error={d.phone && !phoneOk ? 'تنسيق الجوال 05XXXXXXXX' : null}
          >
            <Input
              value={d.phone}
              onChange={(v) => set('phone', v.replace(/\D/g, ''))}
              placeholder="05XXXXXXXX"
              ltr
              maxLength={10}
            />
          </Field>
          <Field
            label="البريد الإلكتروني"
            required
            error={d.email && !emailOk ? 'تحقق من صيغة البريد' : null}
          >
            <Input
              value={d.email}
              onChange={(v) => set('email', v)}
              placeholder="you@lab.com"
              ltr
              type="email"
            />
          </Field>
        </div>

        <Field label="كلمة المرور" required hint="8+ أحرف، مع حرف كبير وصغير ورقم">
          <Input
            value={d.password}
            onChange={(v) => set('password', v)}
            ltr
            type={showPw ? 'text' : 'password'}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRAND.muted, padding: 4, display: 'flex' }}
              >
                <Icon.Eye off={showPw} />
              </button>
            }
          />
          {d.password && (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 4, background: BRAND.surfaceAlt, borderRadius: 2, overflow: 'hidden', display: 'flex', gap: 2 }}>
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      background: i <= pwStrength ? strengthColor : 'transparent',
                      transition: 'background .2s',
                    }}
                  />
                ))}
              </div>
              <span style={{ fontSize: 12, color: strengthColor, fontWeight: 500, minWidth: 48 }}>
                {strengthLabel}
              </span>
            </div>
          )}
        </Field>

        <Field
          label="تأكيد كلمة المرور"
          required
          error={d.confirmPassword && !matchOk ? 'كلمتا المرور غير متطابقتين' : null}
        >
          <Input
            value={d.confirmPassword}
            onChange={(v) => set('confirmPassword', v)}
            ltr
            type={showPw2 ? 'text' : 'password'}
            rightSlot={
              <button
                type="button"
                onClick={() => setShowPw2(!showPw2)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: BRAND.muted, padding: 4, display: 'flex' }}
              >
                <Icon.Eye off={showPw2} />
              </button>
            }
          />
        </Field>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', marginTop: 8 }}>
          <input
            type="checkbox"
            checked={!!d.agree}
            onChange={(e) => set('agree', e.target.checked)}
            style={{ width: 18, height: 18, accentColor: BRAND.green, marginTop: 2, flexShrink: 0 }}
          />
          <span style={{ fontSize: 13.5, color: BRAND.text, lineHeight: 1.6 }}>
            أوافق على{' '}
            <a style={{ color: BRAND.greenDark, fontWeight: 500 }}>شروط الاستخدام</a>
            {' '}و
            <a style={{ color: BRAND.greenDark, fontWeight: 500 }}>سياسة الخصوصية</a>.
          </span>
        </label>
      </Card>
    </Frame>
  );
}
